<script setup lang="ts">
import { computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppShell from '@/components/AppShell.vue'
import { usePrefs } from '@/stores/prefs'
import { useSession } from '@/stores/session'
import { useFloor } from '@/stores/floor'
import { bootClient } from '@/lib/bootstrap'
import { resumeCall } from '@/lib/call'

const prefs = usePrefs()
const session = useSession()
const floor = useFloor()
const route = useRoute()
const router = useRouter()

const bare = computed(() => route.meta.bare === true)

async function boot() {
  if (!session.loggedIn) return
  await bootClient()
  void session.loadProfile()
  void floor.syncActiveRoom(route.name as string | undefined, (route.params.slug as string) || null)
  // Back into the call a reload interrupted — even when the reload happened on
  // the floor or any other view, where no RoomView is around to remount it.
  // (syncActiveRoom above claims activeSlug synchronously, so a reload straight
  // into a different room correctly wins over the resume.)
  resumeCall()
}

onMounted(async () => {
  prefs.apply()

  // Returning from SSO? Exchange the loginToken (it rides in the hash/query).
  const m =
    location.hash.match(/[?&]loginToken=([^&]+)/) || location.search.match(/[?&]loginToken=([^&]+)/)
  if (m) {
    try {
      await session.completeSSO(decodeURIComponent(m[1]))
      history.replaceState(null, '', location.pathname)
      await router.replace({ name: 'floor' })
    } catch {
      await router.replace({ name: 'login' })
    }
  }

  await boot()
})

// Boot once login flips true (covers fresh password login); when it flips false
// — manual sign-out or a token that died under us — leave for the login screen.
watch(() => session.loggedIn, (v) => {
  if (v) void boot()
  else if (route.name !== 'login' && route.name !== 'join') void router.replace({ name: 'login' })
})

// Ambient persistence: drive RTC membership from the route.
watch(
  () => [route.name, route.params.slug],
  () => void floor.syncActiveRoom(route.name as string | undefined, (route.params.slug as string) || null),
)
</script>

<template>
  <router-view v-if="bare" />
  <AppShell v-else>
    <router-view />
  </AppShell>
</template>
